<template>
  <v-dialog
    v-model="show"
    max-width="800px"
  >
    <v-form ref="form" lazy-validation :disabled="saving" @submit="saveCombination" onsubmit="return false;">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ $t('compatibleScenarios.selectCompatibleScenarios') }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="show = false"
          >
          <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <loader v-if="loading"
            :label="$t('compatibleScenarios.searchingMinimalScenarios')"
          />
          <v-container
            v-else
            fluid
          >
            <v-row>
              <v-col>
                <v-data-iterator
                  :items="scenarios"
                  :items-per-page.sync="itemsPerPage"
                  :page.sync="page"
                  hide-default-footer
                >
                  <template v-slot:header>
                    <div
                      class="mx-6 mb-0 pa-4 elevation-0 raison-highlight justity--center"
                    >
                      <template
                        v-if="!(compatibleScenarios && compatibleScenarios.length > 0)"
                      >
                        {{ $t('compatibleScenarios.noScenarioSelected') }}
                      </template>
                      <template
                        else
                      >
                        <div>
                          <div class="rai-wigo">
                            <v-chip
                              v-if="selectedCombination.id"
                              :color="color(selectedCombination.id)"
                              class="ma-1 rai-wigo"
                              small
                              >
                              {{ debugMode ? selectedCombination.id : '' }}
                            </v-chip>
                            <scenario
                              v-if="compatibleScenarios.length > 0"
                              class="ma-1 rai-wigo"
                              :scenario="union"
                              :show-details="false"
                            />
                          </div>
                        </div>
                      </template>
                    </div>
                  </template>

                  <template v-slot:default="props">
                    <v-container
                      v-for="item in props.items"
                      :key="item.id"
                      class="my-4"
                    >
                    <v-hover v-slot="{ hover }">
                      <v-row
                        cols="12"
                        sm="6"
                        md="4"
                        lg="3"
                        align="center"
                        class="elevation-2"
                        :class="{ 'elevation-4 grey lighten-3 clickable': hover }"
                        >
                        <v-col cols="8" class="pa-1">
                          <scenario
                            :scenario="item"
                            :show-details="debugMode"
                          />
                        </v-col>
                        <v-col cols="3" class="pa-0">
                          <v-chip
                            v-for="union in item.compatibleScenarios"
                            :key="item.id + '-' + union"
                            :color="color(union)"
                            class="ma-1"
                            small
                          >
                            {{ debugMode ? union : '' }}
                          </v-chip>
                        </v-col>
                        <v-col cols="1" class="pa-0">
                          <v-btn
                            :color="item.selected ? 'green' : 'grey'"
                            @click="$event => addOrRemove(item)"
                            fab
                            small
                            dark
                          >
                              <v-icon>mdi-link-plus</v-icon>
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-hover>
                    </v-container>
                  </template>

                  <template v-slot:footer>
                    <v-row
                      class="mt-2"
                      align="center"
                      justify="center"
                    >
                      <span class="grey--text caption">{{ $t('global.rowPerPage') }}</span>
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            dark
                            text
                            color="grey"
                            class="ml-2 caption"
                            v-bind="attrs"
                            v-on="on"
                          >
                            {{ itemsPerPage }}
                            <v-icon>mdi-chevron-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item
                            v-for="(number, index) in itemsPerPageArray"
                            :key="index"
                            @click="updateItemsPerPage(number)"
                          >
                            <v-list-item-title>{{ number }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>

                      <span
                        class="mr-4 grey--text caption"
                      >
                        {{ legend }}
                      </span>
                      <v-btn
                        fab
                        dark
                        color="#062b89"
                        class="mr-1"
                        @click="formerPage"
                        small
                        :disabled="isFirstPage"
                      >
                        <v-icon>mdi-chevron-left</v-icon>
                      </v-btn>
                      <v-btn
                        fab
                        dark
                        color="#062b89"
                        class="ml-1"
                        @click="nextPage"
                        small
                        :disabled="isLastPage"
                      >
                        <v-icon>mdi-chevron-right</v-icon>
                      </v-btn>
                    </v-row>
                  </template>
                </v-data-iterator>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="!isNew"
            class="raison-btn"
            @click="deleteCombination"
            :disabled="saving"
            :loading="saving"
            alt="delete"
          >
            <v-icon dark>
              mdi-trash-can-outline
            </v-icon>
            {{ $t('editor.option.delete') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            type="submit"
            class="raison-btn"
            :disabled="saving || !valid"
            :loading="saving"
          >
            <v-icon dark>
              mdi-content-save-outline
            </v-icon>
            {{ $t('editor.option.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
</v-dialog>
</template>
<script>
import Loader from '@/components/global/Loader'
import Scenario from '@/components/global/Scenario'

import pageableMixin from '@/mixins/pageable-mixin.js'
import debugModeMixin from '@/mixins/debug-mode-mixin.js'

import scenarioService from '@/services/scenarios.js'

export default {
  name: 'IdentifyCompatibleScenariosDialog',

  components: {
    Loader,
    Scenario,
  },

  mixins: [ debugModeMixin, pageableMixin ],

  data () {
    return {
      loading: false,
      saving: false,
      scenarios: [],
      compatibleScenarios: [],
      selectedCombination: {}
    };
  },

  props: {
    open: {
      type: Boolean,
      required: true,
    },
    combination: {
      type: Object,
      required: false,
      default: undefined
    }
  },

  watch: {
    open(value) {
      if(value) {
        this.findScenarios()
      }
    },
    combination(combination) {
      this.selectedCombination = JSON.parse(JSON.stringify(combination))
      this.compatibleScenarios = this.selectedCombination.scenarios || []
    }
  },

  computed: {
    show: {
      get() {
          return this.open;
      },
      set(value) {
        if(!value) {
          this.onCancel()
        }
        this.$emit('update:open', value);
      }
    },
    isNew() {
      return !this.selectedCombination.id
    },
    valid() {
      return this.compatibleScenarios && this.compatibleScenarios.length >= 2
    },
    union() {
      if(this.compatibleScenarios.length == 0) {
        return {}
      }
      return scenarioService.unionAsScenario({
        scenarios: this.compatibleScenarios
      })
    }
  },

  methods: {
    onCancel() {
      this.page = 1
      if(Object.keys(this.combination).length > 0) {
        this.selectedCombination = JSON.parse(JSON.stringify(this.combination))
        this.compatibleScenarios = JSON.parse(JSON.stringify(this.selectedCombination.scenarios))
      }
      this.$refs.form.resetValidation()
    },
    saveCombination(event) {
      event.preventDefault()
      console.log('saveCombination')
      const projectId = this.$route.params.id
      const scenarioIds = this.compatibleScenarios.map(scenario => scenario.id)
      this.saving = true
      if(this.isNew) {
        return scenarioService.createCompatibleScenariosUnion(projectId, scenarioIds)
          .then(response => {
            console.log('saveCombination', response)
            this.$emit('scenariosCombinationCreated', response.data)
            this.show = false
          })
          .catch(err => {
            this.$emit('raison-event', { type: 'error', messages: [err] })
            throw err
          })
          .finally(() => {
            this.onCancel()
            this.saving = false
          })
      }
      else {
        const scenarioIds = this.compatibleScenarios.map(scenario => scenario.id)
        return scenarioService.updateCompatibleScenariosUnion(projectId, this.selectedCombination.id, scenarioIds)
          .then(response => {
            console.log('saveCombination', response)
            this.$emit('scenariosCombinationUpdated', this.selectedCombination)
            this.show = false
          })
          .catch(err => {
            this.$emit('raison-event', { type: 'error', messages: [err] })
            throw err
          })
          .finally(() => {
            this.onCancel()
            this.saving = false
          })
      }
    },
    deleteCombination() {
      console.log('deleteCombination')
      const projectId = this.$route.params.id
      this.saving = true
      scenarioService.deleteCompatibleScenariosUnion(projectId, this.selectedCombination.id)
        .then(() => {
            this.$emit('scenariosCombinationDeleted', this.selectedCombination)
            this.show = false
          })
          .catch(err => {
            this.$emit('raison-event', { type: 'error', messages: [err] })
          })
          .finally(() => {
            this.saving = false
          })
    },
    addOrRemove(scenario) {
      if(!scenario.selected) {
        this.compatibleScenarios.push(scenario)
      }
      else {
        const index = this.compatibleScenarios.findIndex(sce => scenario.id === sce.id)
        if(index >= 0) {
          this.compatibleScenarios.splice(index, 1)
        }
      }
      scenario.selected = !scenario.selected
    },
    color(unionId) {
      return scenarioService.mapColor(unionId)
    },
    findScenarios() {
      this.loading = true
      const projectId = this.$route.params.id

      const services = [
        scenarioService.findMinimalScenarios(projectId, this.page, this.itemsPerPage)
      ]

      Promise.all(services)
        .then((responses) => {
          this.scenarios = responses[0].data.content.map(sce => Object.assign(sce, { selected: false }))
          this.page = responses[0].data.page.number + 1
          this.numberOfPages = responses[0].data.page.totalPages
          this.isLastPage = responses[0].data.page.last
          this.totalNumberOfItems = responses[0].data.page.totalElements
          this.scenarios.map(scenario => scenario.selected = this.compatibleScenarios.find(sce => sce.id === scenario.id))
        })
        .catch(err => {
          this.$emit('raison-event', { type:'error', messages:[err] });
        })
        .finally(() => {
          this.loading = false;
        });

    },
    nextPage() {
      if (this.page + 1 <= this.numberOfPages) {
        this.page += 1
        this.findScenarios()
      }
    },
    formerPage() {
      if (this.page - 1 >= 1) {
        this.page -= 1
        this.findScenarios()
      }
    },
    updateItemsPerPage(number) {
      this.itemsPerPage = number
      this.page = 1
      this.findScenarios()
    },
  },

}
</script>
<style>
.rai-selectable-scenario {
  cursor: pointer;
}
</style>